import React from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, Link } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';
import BlockContent from '@sanity/block-content-to-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { COUNTRY_CODE, HIDDEN_FEATURES, PRODUCT_TYPE_COLOR_TO_BARS_IMAGE } from '../constants';
import { RawPortableText, GatsbySanityImageFluidNoBase64, GatsbySanityImageFluid } from '../types';
import {
  sortBy,
  getPortableTextAsString,
  convertRToSuperscript,
  getProblemCommonAndOrScientificName,
} from '../utils/utils';
import { ResourceFile } from '../graphql-fragments/ResourceFileFragment';
import SEO from '../components/SEO';
import DetailsPageLayout from '../components/DetailsPageLayout';
import serializers from '../serializers';
import GenericLink from '../components/GenericLink';
import Video from '../components/Video';
import FileRow from '../components/FileRow';
import Accordion from '../components/Accordion';

import explosive from '../assets/images/ghs/ghsCode_01_large.png';
import flammable from '../assets/images/ghs/ghsCode_02_large.png';
import oxidising from '../assets/images/ghs/ghsCode_03_large.png';
import compressedGas from '../assets/images/ghs/ghsCode_04_large.png';
import corrosive from '../assets/images/ghs/ghsCode_05_large.png';
import toxic from '../assets/images/ghs/ghsCode_06_large.png';
import harmful from '../assets/images/ghs/ghsCode_07_large.png';
import healthHazard from '../assets/images/ghs/ghsCode_08_large.png';
import environmentalHazard from '../assets/images/ghs/ghsCode_09_large.png';
import leaf from '../assets/images/leaf.svg';

import * as styles from './ProductPage.module.scss';
import ProductCard from '../components/ProductCard';
import ImageDownload from '../components/ImageDownload';
import NewsCard from '../components/NewsCard';
import { NewsArticle } from '../pages/news';

type ID = string;

interface GhsCodeAndTexts {
  ghsCode: {
    code: string;
    description: string;
  };
  placeholderTexts?: string | null;
  overrideText?: string | null;
}

interface RelatedProduct {
  _id: ID;
  name: string;
  slug: {
    current: string;
  };
  logoImage?: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  };
  image?: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  };
  type: {
    _id: ID;
    brand: 'ascenza' | 'blexia' | null;
    color: string;
    name: string;
  };
  activeSubstanceInfos?: Array<{
    activeSubstance: {
      _id: ID;
      name: string;
    };
  }>;
  problemLabel?: string;
  problemDescription?: string;
  cropDescription?: string;
  applications: Array<{
    crop: {
      _id: ID;
    };
    cropApplications?: Array<{
      problem: {
        _id: ID;
      };
    }>;
  }>;
}

interface DetailedProduct {
  _id: string;
  name: string;
  slug: {
    current: string;
  };
  logoImage?: {
    asset?: {
      url: string;
      fluid: GatsbySanityImageFluidNoBase64;
    };
  };
  image?: {
    asset?: {
      url: string;
      fluid: GatsbySanityImageFluidNoBase64;
    };
  };
  showOrganicTag?: boolean;
  type: {
    _id: string;
    brand: 'ascenza' | 'blexia' | null;
    name: string;
    color: string;
  };
  leadCrop?: {
    _id: string;
  };
  leadActiveSubstance?: {
    _id: string;
  };
  activeSubstanceInfos?: Array<{
    _key: string;
    activeSubstance: {
      name: string;
    };
    concentrationPercent?: string;
    concentrationGPerL?: string;
    concentrationGPerKg?: string;
    note?: string;
  }>;
  chemicalFamily?: string;
  formulationNote?: string;
  formulationType?: {
    description: string;
    code: string;
  };
  packing?: Array<string>;
  brochureDocument?: ResourceFile;
  securitySheetDocument?: ResourceFile;
  securitySheetDocumentUrl?: string;
  technicalSheetDocument?: ResourceFile;
  labelDocument?: ResourceFile;
  extraDocument?:
    | {
        title: string;
        file: ResourceFile;
      }
    | Record<string, never>;
  otherDocuments?: Array<{
    title: string;
    file: ResourceFile;
  }>;
  registrationDocumentUrl?: string;
  registrationNumber?: string;
  certifications?: Array<{
    _key: string;
    name: string;
    logo: {
      asset: {
        url: string;
      };
    };
  }>;

  toxicologicalDangerLabel: string;
  toxicologicalGHSPictograms?: Array<string>;
  ghsCodesAndTexts?: Array<GhsCodeAndTexts>;

  promotionalVideoUrl?: string;
  _rawUnstructuredContent?: RawPortableText;
  _rawSummaryText?: RawPortableText;
  _rawMainCharacteristicsText?: RawPortableText;
  _rawObservationsText?: RawPortableText;
  _rawComplementaryInformationsText?: RawPortableText;
  _rawAdvantagesText?: RawPortableText;
  _rawDeclaredContentText?: RawPortableText;
  _rawTrademarkText?: RawPortableText;

  _rawApplicationModeText?: RawPortableText;
  _rawPreparationModeText?: RawPortableText;
  _rawBiologicalPrecautionsText?: RawPortableText;
  _rawMinorUsesText?: RawPortableText;
  _rawSafetyIntervalText?: RawPortableText;
  _rawCompatibilitiesText?: RawPortableText;
  _rawSusceptibleWeedsText?: RawPortableText;
  _rawResistantWeedsText?: RawPortableText;

  applications?: Array<{
    _key: string;
    crop: {
      _id: string;
      name: string;
    };
    cropApplications?: Array<{
      _key: string;
      problem?: {
        group: {
          name: string;
        };
      } & (
        | {
            name: string;
            scientificName?: null;
          }
        | {
            name?: null;
            scientificName: string;
          }
        | {
            name: string;
            scientificName: string;
          }
      );
      dosage?: string;
      safetyPeriod?: string;
      observation?: string;
    }>;
  }>;
  applicationsExternalDatabaseUrl?: string;
  applicationsInternalDocuments?: Array<ResourceFile>;
  applicationsExternalDocuments?: Array<{
    title: string;
    url: string;
  }>;

  gallery: {
    galleryImages: Array<{
      label1: string;
      label2: string;
      image: {
        asset: {
          url: string;
          extension: string;
        };
      };
    }>;

    galleryVideos: Array<{
      name: string;
      url: string;
      description: string;
    }>;
  };
}

interface ProductPageProps {
  data: {
    sanityProduct: DetailedProduct;
    sanityProductDetailsPage: {
      emergencyNumber?: string;
      moreInformationArticle?: {
        title: string;
        slug: {
          current: string;
        };
      };
      _rawLegalNotice?: string;
      disclaimer?: string;
      disclaimerArticle?: {
        title: string;
        slug: {
          current: string;
        };
      };
    };
  };
  pageContext: {
    relatedProducts: Array<RelatedProduct>;
    relatedNewsArticles: Array<NewsArticle>;
  };
}

export const query = graphql`
  # "-c6cb7971-1930-5c41-ac83-70b0247c1eb9"
  query GetProductById($id: String!) {
    sanityProduct(id: { eq: $id }) {
      _id
      name
      slug {
        current
      }
      logoImage {
        asset {
          url
          fluid(maxWidth: 400) {
            ...GatsbySanityImageFluid_noBase64
          }
        }
      }
      image {
        asset {
          url
          fluid(maxWidth: 400) {
            ...GatsbySanityImageFluid_noBase64
          }
        }
      }
      showOrganicTag
      type {
        _id
        brand
        name
        color
      }
      leadCrop {
        _id
      }
      leadActiveSubstance {
        _id
      }
      activeSubstanceInfos {
        _key
        activeSubstance {
          name
        }
        concentrationPercent
        concentrationGPerL
        concentrationGPerKg
        note
      }
      chemicalFamily
      formulationNote
      formulationType {
        description
        code
      }
      packing
      brochureDocument {
        ...ResourceFile
      }
      securitySheetDocument {
        ...ResourceFile
      }
      securitySheetDocumentUrl
      technicalSheetDocument {
        ...ResourceFile
      }
      labelDocument {
        ...ResourceFile
      }
      extraDocument {
        title
        file {
          ...ResourceFile
        }
      }
      otherDocuments {
        title
        file {
          ...ResourceFile
        }
      }
      registrationDocumentUrl
      registrationNumber
      certifications {
        _key
        name
        logo {
          asset {
            url
          }
        }
      }

      # toxicological information
      toxicologicalDangerLabel
      toxicologicalGHSPictograms
      ghsCodesAndTexts {
        ghsCode {
          code
          description
        }
        placeholderTexts
        overrideText
      }

      # product description
      promotionalVideoUrl
      _rawUnstructuredContent(resolveReferences: { maxDepth: 4 })
      _rawSummaryText(resolveReferences: { maxDepth: 4 })
      _rawMainCharacteristicsText(resolveReferences: { maxDepth: 4 })
      _rawObservationsText(resolveReferences: { maxDepth: 4 })
      _rawComplementaryInformationsText(resolveReferences: { maxDepth: 4 })
      _rawAdvantagesText(resolveReferences: { maxDepth: 4 })
      _rawDeclaredContentText(resolveReferences: { maxDepth: 4 })
      _rawTrademarkText(resolveReferences: { maxDepth: 4 })

      # application information
      _rawApplicationModeText(resolveReferences: { maxDepth: 4 })
      _rawPreparationModeText(resolveReferences: { maxDepth: 4 })
      _rawBiologicalPrecautionsText(resolveReferences: { maxDepth: 4 })
      _rawMinorUsesText(resolveReferences: { maxDepth: 4 })
      _rawSafetyIntervalText(resolveReferences: { maxDepth: 4 })
      _rawCompatibilitiesText(resolveReferences: { maxDepth: 4 })
      _rawSusceptibleWeedsText(resolveReferences: { maxDepth: 4 })
      _rawResistantWeedsText(resolveReferences: { maxDepth: 4 })

      # application by crop
      applications {
        _key
        crop {
          _id
          name
        }
        cropApplications {
          _key
          problem {
            name
            group {
              name
            }
            scientificName
          }
          dosage
          safetyPeriod
          observation
        }
      }
      applicationsExternalDatabaseUrl
      applicationsInternalDocuments {
        ...ResourceFile
      }
      applicationsExternalDocuments {
        title
        url
      }

      gallery {
        galleryImages {
          label1
          label2
          image {
            asset {
              url
              extension
            }
          }
        }
        galleryVideos {
          name
          url
          description
        }
      }
    }
    sanityProductDetailsPage {
      emergencyNumber
      moreInformationArticle {
        title
        slug {
          current
        }
      }
      _rawLegalNotice
      disclaimer
      disclaimerArticle {
        title
        slug {
          current
        }
      }
    }
  }
`;

const ProductPage = ({
  data,
  pageContext: { relatedProducts, relatedNewsArticles },
}: ProductPageProps): React.ReactElement => {
  const { t } = useTranslation();
  const { sanityProduct: product } = data;
  const productDetailsPage = data.sanityProductDetailsPage || {};

  function renderToxicologicalDangerLabel(labelId: string) {
    let label;
    if (labelId === 'none') {
      return;
    } else if (labelId === 'danger') {
      label = t('product_page.toxicological_information.danger_label', 'Danger');
    } else if (labelId === 'attention') {
      label = t('product_page.toxicological_information.attention_label', 'Attention');
    } else {
      throw new Error('Unrecognized toxicological label: ' + label);
    }
    return <h4 className={styles.dangerLabel}>{label}</h4>;
  }

  function renderPictogram(pictogram: string) {
    if (pictogram === 'gsh01-explosive') {
      return <img className={styles.pictogram} src={explosive} alt="Explosive GSH Pictogram" />;
    } else if (pictogram === 'gsh02-flammable') {
      return <img className={styles.pictogram} src={flammable} alt="Flammable GSH Pictogram" />;
    } else if (pictogram === 'gsh03-oxidising') {
      return <img className={styles.pictogram} src={oxidising} alt="Oxidising GSH Pictogram" />;
    } else if (pictogram === 'gsh04-compressed-gas') {
      return (
        <img className={styles.pictogram} src={compressedGas} alt="Compressed Gas GSH Pictogram" />
      );
    } else if (pictogram === 'gsh05-corrosive') {
      return <img className={styles.pictogram} src={corrosive} alt="Corrosive GSH Pictogram" />;
    } else if (pictogram === 'gsh06-toxic') {
      return <img className={styles.pictogram} src={toxic} alt="Toxic GSH Pictogram" />;
    } else if (pictogram === 'gsh07-harmful') {
      return <img className={styles.pictogram} src={harmful} alt="Harmful GSH Pictogram" />;
    } else if (pictogram === 'gsh08-health-hazard') {
      return (
        <img className={styles.pictogram} src={healthHazard} alt="Health Hazard GSH Pictogram" />
      );
    } else if (pictogram === 'gsh09-environmental-hazard') {
      return (
        <img
          className={styles.pictogram}
          src={environmentalHazard}
          alt="Environmental Hazard GSH Pictogram"
        />
      );
    } else {
      throw new Error('Got unrecognized pictogram: ' + pictogram);
    }
  }

  function renderApplicationsByCrop() {
    const cleanApplications = sortBy(
      (product.applications || [])
        .map(application => ({
          ...application,
          cropApplications: application.cropApplications
            ? sortBy(
                application.cropApplications.filter(cropApplication => !!cropApplication.problem),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                cropApplication => getProblemCommonAndOrScientificName(cropApplication.problem!),
              )
            : [],
        }))
        .filter(application => application.cropApplications.length > 0),
      application => application.crop.name,
    );

    return (
      cleanApplications.length > 0 && (
        <Accordion title={t('product_page.applications_by_crop.title', 'Applications by crop')}>
          {cleanApplications.map((application, i) => (
            <div key={i} className={styles.container}>
              <div className={styles.leftSide}>
                <h4>{application.crop.name}</h4>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.applicationsContainer}>
                  {application.cropApplications.map(cropApplication => (
                    <div className={styles.application} key={cropApplication._key}>
                      <div className={styles.problemContainer}>
                        <div className={styles.problemBlock}>
                          <span className={styles.label}>
                            {t('product_page.applications_by_crop.problem_label', 'Problem')}
                          </span>
                          {!cropApplication.problem!.name &&
                            cropApplication.problem!.scientificName && (
                              <span className={styles.scientificName}>
                                {cropApplication.problem!.scientificName}
                              </span>
                            )}
                          {cropApplication.problem!.name &&
                            !cropApplication.problem!.scientificName && (
                              <span>{cropApplication.problem!.name}</span>
                            )}
                          {cropApplication.problem!.name &&
                            cropApplication.problem!.scientificName && (
                              <span>
                                {cropApplication.problem!.name}{' '}
                                <span className={styles.scientificName}>
                                  {' '}
                                  ({cropApplication.problem!.scientificName})
                                </span>
                              </span>
                            )}
                        </div>

                        {!!cropApplication.dosage && (
                          <div className={styles.problemBlock}>
                            <span className={styles.label}>
                              {t('product_page.applications_by_crop.dosage_label', 'Dosage')}
                            </span>
                            <span className={styles.info}>{cropApplication.dosage}</span>
                          </div>
                        )}

                        {!!cropApplication.safetyPeriod && (
                          <div className={styles.problemBlock}>
                            <span className={styles.label}>
                              {t(
                                'product_page.applications_by_crop.safety_period_label',
                                'Safety Period',
                              )}
                            </span>
                            <span className={styles.info}>{cropApplication.safetyPeriod}</span>
                          </div>
                        )}
                      </div>
                      {!!cropApplication.observation && (
                        <div className={styles.problemObservation}>
                          <span className={styles.label}>
                            {t(
                              'product_page.applications_by_crop.observations_label',
                              'Observations',
                            )}
                          </span>
                          <span className={styles.info}>
                            {convertRToSuperscript(cropApplication.observation)}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </Accordion>
      )
    );
  }

  function renderApplicationsFr() {
    const hasCrops = !!product.applications && product.applications.length > 0;
    const hasDBLink = !!product.applicationsExternalDatabaseUrl;
    const hasInternalDocuments =
      product.applicationsInternalDocuments && product.applicationsInternalDocuments.length > 0;
    const hasExternalDocuments =
      product.applicationsExternalDocuments && product.applicationsExternalDocuments.length > 0;
    const hasDocuments = hasExternalDocuments || hasInternalDocuments;
    const hasApplications = hasCrops || hasDBLink || hasDocuments;

    return (
      hasApplications && (
        <Accordion title={t('product_page.applications_fr.title', 'Applications')}>
          {hasCrops && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.applications_fr.crops_label', 'Crops')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.applicationsContainerFR}>
                  {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                  {sortBy(product.applications || [], application => application.crop.name).map(
                    (application, i) => (
                      <span className={styles.cropInfo} key={i} title={application.crop.name}>
                        {application.crop.name}
                      </span>
                    ),
                  )}
                </div>
              </div>
            </div>
          )}

          {(hasDBLink || hasDocuments) && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.applications_fr.more_information_label', 'More Information')}
                </h4>
              </div>

              <div className={styles.rightSide}>
                <div className={styles.applicationsFRMoreInformation}>
                  {hasDBLink && (
                    <div className={styles.infoFR}>
                      <span className={styles.label}>
                        {t(
                          'product_page.applications_fr.external_applications_db_label',
                          'National Products Database',
                        )}
                      </span>
                      {/* TODO Add external link icon */}
                      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                      <GenericLink to={product.applicationsExternalDatabaseUrl!}>
                        {t(
                          'product_page.applications_fr.external_applications_db_link_text',
                          'Compreensive list of all applications',
                        )}
                      </GenericLink>
                    </div>
                  )}

                  {hasDocuments && (
                    <div className={styles.infoFR}>
                      <span className={styles.label}>
                        {t(
                          'product_page.applications_fr.complementary_documents_label',
                          'Complementary Documents',
                        )}
                      </span>
                      {hasInternalDocuments &&
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        product
                          .applicationsInternalDocuments!.filter(
                            document => document && document.file && document.file.asset,
                          )
                          .map((document, i) => (
                            <FileRow
                              key={i}
                              small
                              title={document.title}
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              url={document.file!.asset!.url}
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              size={document.file!.asset!.size}
                            ></FileRow>
                          ))}
                      {hasExternalDocuments &&
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        product.applicationsExternalDocuments!.map((documentUrl, i) => (
                          <FileRow
                            key={i}
                            small
                            noDownload
                            title={documentUrl.title}
                            url={documentUrl.url}
                          ></FileRow>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Accordion>
      )
    );
  }

  function getFinalGhsCodeAndDescription({
    ghsCode,
    placeholderTexts,
    overrideText,
  }: GhsCodeAndTexts) {
    let finalDescription;
    placeholderTexts = placeholderTexts || '';
    overrideText = overrideText || '';

    if (overrideText.trim()) {
      finalDescription = overrideText.trim();
    } else if (!ghsCode.description.match(/__+/)) {
      finalDescription = ghsCode.description;
    } else {
      const descriptionTextsArray = ghsCode.description.split(/__+/);
      const placeholdersTextsArray = placeholderTexts.split('|');
      finalDescription = descriptionTextsArray[0];
      for (let i = 0; i < placeholdersTextsArray.length; i++) {
        finalDescription += placeholdersTextsArray[i] + descriptionTextsArray[i + 1];
      }
    }

    return { code: ghsCode.code, description: finalDescription };
  }

  if (!Object.keys(PRODUCT_TYPE_COLOR_TO_BARS_IMAGE).includes(product.type.color)) {
    throw new Error('Got unknown product.type.color: ' + product.type.color);
  }

  const productTypeColor = product.type.color as keyof typeof PRODUCT_TYPE_COLOR_TO_BARS_IMAGE;

  return (
    <DetailsPageLayout
      turnBackLink={{ to: '/products/', title: t('product_page_return_link', 'All products') }}
      barsImage={PRODUCT_TYPE_COLOR_TO_BARS_IMAGE[productTypeColor]}
      title={product.name}
      blexiaProduct={product.type.brand === 'blexia'}
      fullWithPage
      borderColor={product.type.color}
      imageTitle={product.logoImage && product.logoImage.asset && product.logoImage.asset.fluid}
    >
      <SEO
        title={product.name}
        description={
          product._rawSummaryText ? getPortableTextAsString(product._rawSummaryText) : undefined
        }
        urlPath={'/products/' + product.slug.current}
        imageUrl={
          product.image && product.image.asset
            ? product.image.asset.url + '?w=1200&h=630&fit=max'
            : undefined
        }
      />
      <div
        className={
          styles.featuresSectionContainer +
          (product.type.brand === 'blexia' ? ' ' + styles.blexiaBrand : '')
        }
      >
        {!!(product.image && product.image.asset) && (
          <div className={styles.topLeftSide}>
            <Img
              className={styles.image}
              loading="eager"
              fadeIn={false}
              fluid={product.image.asset.fluid}
              alt={product.name}
              objectFit="contain"
            />
          </div>
        )}
        <div className={styles.topRightSide}>
          <div className={styles.featuresContainerWrapper}>
            <div className={styles.featuresContainer}>
              {/* TYPE */}
              {!!product.type && (
                <div className={styles.feature}>
                  <span className={styles.label}>{t('product_page.type_label', 'Type')}</span>
                  <span className={styles.info}>{product.type.name}</span>
                </div>
              )}

              {/* FORMULATION */}
              {((!!product.activeSubstanceInfos && product.activeSubstanceInfos.length > 0) ||
                !!product.formulationNote) && (
                <div className={styles.feature}>
                  <span className={styles.label}>
                    {t('product_page.formulation_label', 'Formulation')}
                  </span>
                  <span className={styles.info}>
                    {[
                      product.activeSubstanceInfos &&
                        product.activeSubstanceInfos
                          .map(
                            activeSubstanceInfo =>
                              activeSubstanceInfo.activeSubstance.name +
                              (activeSubstanceInfo.concentrationPercent
                                ? ' ' + activeSubstanceInfo.concentrationPercent + '%'
                                : '') +
                              ((activeSubstanceInfo.concentrationGPerL &&
                                ` (=${activeSubstanceInfo.concentrationGPerL} g/l)`) ||
                                (activeSubstanceInfo.concentrationGPerKg &&
                                  ` (=${activeSubstanceInfo.concentrationGPerKg} g/kg)`) ||
                                '') +
                              (activeSubstanceInfo.note ? ' ' + activeSubstanceInfo.note : ''),
                          )
                          .join(', '),
                      product.formulationNote,
                    ]
                      .filter(Boolean)
                      .join('. ')}
                  </span>
                </div>
              )}

              {!!product.chemicalFamily && (
                <div className={styles.feature}>
                  <span className={styles.label}>
                    {t('product_page.chemical_family_label', 'Chemical Family')}
                  </span>
                  <span className={styles.info}>{product.chemicalFamily}</span>
                </div>
              )}

              {!!product.formulationType && (
                <div className={styles.feature + ' ' + styles.formulationTypeLeftSide}>
                  <span className={styles.label}>
                    {t('product_page.formulation_type_label', 'Formulation Type')}
                  </span>
                  <span className={styles.info}>
                    {product.formulationType.description +
                      ' ' +
                      '(' +
                      product.formulationType.code +
                      ')'}
                  </span>
                </div>
              )}

              {!!product.certifications && product.certifications.length > 0 && (
                <div className={styles.feature}>
                  <span className={styles.label}>
                    {t('product_page.certifications_label', 'Certifications')}
                  </span>
                  <div className={styles.certifications}>
                    {product.certifications.map((certification, i) => (
                      <img
                        key={i}
                        className={styles.certificationLogo}
                        src={certification.logo.asset.url + '?h=120&fit=min'}
                        alt={certification.name}
                      />
                    ))}
                  </div>
                </div>
              )}

              {!!product.showOrganicTag && (
                <div className={styles.organicTagContainer}>
                  <div className={styles.organicTagIcon}>
                    <img src={leaf} alt=""></img>
                  </div>
                  <span className={styles.organicTag}>
                    {t('product_page.show_organic_tag', 'Use in organic farming')}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.featuresContainer}>
              {!!product.formulationType && (
                <div className={styles.feature + ' ' + styles.formulationTypeRightSide}>
                  <span className={styles.label}>
                    {t('product_page.formulation_type_label', 'Formulation Type')}
                  </span>
                  <span className={styles.info}>
                    {product.formulationType.description +
                      ' ' +
                      '(' +
                      product.formulationType.code +
                      ')'}
                  </span>
                </div>
              )}

              {!!product.registrationNumber && (
                <div className={styles.feature}>
                  <span className={styles.label}>
                    {t('product_page.registration_number_label', 'Registration Number')}
                  </span>
                  <span className={styles.info}>{product.registrationNumber}</span>
                </div>
              )}
              {!!product.packing && product.packing.length > 0 && (
                <div className={styles.feature}>
                  <span className={styles.label}>{t('product_page.packing_label', 'Packing')}</span>
                  <div className={styles.info}>
                    {product.packing.map((pack, i) => (
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      <span key={i}>{i !== product.packing!.length - 1 ? pack + ', ' : pack}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className={styles.documentsContainer}>
                <div>
                  {!!(
                    product.brochureDocument &&
                    product.brochureDocument.file &&
                    product.brochureDocument.file.asset
                  ) && (
                    <FileRow
                      small
                      title={t('product_page.brochure_label', 'Brochure')}
                      size={product.brochureDocument.file.asset.size}
                      url={product.brochureDocument.file.asset.url}
                    ></FileRow>
                  )}
                </div>
                <div>
                  {!!(
                    product.technicalSheetDocument &&
                    product.technicalSheetDocument.file &&
                    product.technicalSheetDocument.file.asset
                  ) && (
                    <FileRow
                      small
                      title={t('product_page.technical_sheet_label', 'Technical Sheet')}
                      size={product.technicalSheetDocument.file.asset.size}
                      url={product.technicalSheetDocument.file.asset.url}
                    ></FileRow>
                  )}
                </div>
                <div>
                  {!!(
                    product.securitySheetDocument &&
                    product.securitySheetDocument.file &&
                    product.securitySheetDocument.file.asset
                  ) && (
                    <FileRow
                      small
                      title={t('product_page.security_sheet_label', 'Security Sheet')}
                      size={product.securitySheetDocument.file.asset.size}
                      url={product.securitySheetDocument.file.asset.url}
                    ></FileRow>
                  )}
                </div>
                <div>
                  {!!product.securitySheetDocumentUrl && (
                    <FileRow
                      small
                      noDownload
                      title={t('product_page.security_sheet_url_label', 'Security Sheet Url')}
                      url={product.securitySheetDocumentUrl}
                    ></FileRow>
                  )}
                </div>
                <div>
                  {!!(
                    product.labelDocument &&
                    product.labelDocument.file &&
                    product.labelDocument.file.asset
                  ) && (
                    <FileRow
                      small
                      title={t('product_page.label_document_label', 'Label Document')}
                      size={product.labelDocument.file.asset.size}
                      url={product.labelDocument.file.asset.url}
                    ></FileRow>
                  )}
                </div>
                <div>
                  {!!(
                    product.extraDocument &&
                    product.extraDocument.title &&
                    product.extraDocument.file &&
                    product.extraDocument.file.file &&
                    product.extraDocument.file.file.asset
                  ) && (
                    <FileRow
                      small
                      title={product.extraDocument.title}
                      size={product.extraDocument.file.file.asset.size}
                      url={product.extraDocument.file.file.asset.url}
                    ></FileRow>
                  )}
                </div>
                <div>
                  {!!product.registrationDocumentUrl && (
                    <FileRow
                      small
                      noDownload
                      title={t('product_page.registration_url_label', 'Registration Url')}
                      url={product.registrationDocumentUrl}
                    ></FileRow>
                  )}
                </div>
              </div>
            </div>
          </div>
          {!!product.showOrganicTag && (
            <div className={styles.organicTagContainerMobile}>
              <div className={styles.organicTagIcon}>
                <img src={leaf} alt=""></img>
              </div>
              <span className={styles.organicTag}>
                {t('product_page.show_organic_tag', 'Use in organic farming')}
              </span>
            </div>
          )}
          <div className={styles.documentsContainerFullWidth}>
            <div>
              {!!(
                product.brochureDocument &&
                product.brochureDocument.file &&
                product.brochureDocument.file.asset
              ) && (
                <FileRow
                  small
                  title={t('product_page.brochure_label', 'Brochure')}
                  size={product.brochureDocument.file.asset.size}
                  url={product.brochureDocument.file.asset.url}
                ></FileRow>
              )}
            </div>
            <div>
              {!!(
                product.technicalSheetDocument &&
                product.technicalSheetDocument.file &&
                product.technicalSheetDocument.file.asset
              ) && (
                <FileRow
                  small
                  title={t('product_page.technical_sheet_label', 'Technical Sheet')}
                  size={product.technicalSheetDocument.file.asset.size}
                  url={product.technicalSheetDocument.file.asset.url}
                ></FileRow>
              )}
            </div>
            <div>
              {!!(
                product.securitySheetDocument &&
                product.securitySheetDocument.file &&
                product.securitySheetDocument.file.asset
              ) && (
                <FileRow
                  small
                  title={t('product_page.security_sheet_label', 'Security Sheet')}
                  size={product.securitySheetDocument.file.asset.size}
                  url={product.securitySheetDocument.file.asset.url}
                ></FileRow>
              )}
            </div>
            <div>
              {!!product.securitySheetDocumentUrl && (
                <FileRow
                  small
                  noDownload
                  title={t('product_page.security_sheet_url_label', 'Security Sheet Url')}
                  url={product.securitySheetDocumentUrl}
                ></FileRow>
              )}
            </div>
            <div>
              {!!(
                product.labelDocument &&
                product.labelDocument.file &&
                product.labelDocument.file.asset
              ) && (
                <FileRow
                  small
                  title={t('product_page.label_document_label', 'Label Document')}
                  size={product.labelDocument.file.asset.size}
                  url={product.labelDocument.file.asset.url}
                ></FileRow>
              )}
            </div>
            <div>
              {!!(
                product.extraDocument &&
                product.extraDocument.title &&
                product.extraDocument.file &&
                product.extraDocument.file.file &&
                product.extraDocument.file.file.asset
              ) && (
                <FileRow
                  small
                  title={product.extraDocument.title}
                  size={product.extraDocument.file.file.asset.size}
                  url={product.extraDocument.file.file.asset.url}
                ></FileRow>
              )}
            </div>
            <div>
              {!!product.registrationDocumentUrl && (
                <FileRow
                  small
                  noDownload
                  title={t('product_page.registration_url_label', 'Registration Url')}
                  url={product.registrationDocumentUrl}
                ></FileRow>
              )}
            </div>
          </div>
        </div>
      </div>
      {(product.toxicologicalDangerLabel !== 'none' ||
        (!!product.toxicologicalGHSPictograms && product.toxicologicalGHSPictograms.length > 0) ||
        (!!product.ghsCodesAndTexts && product.ghsCodesAndTexts.length > 0) ||
        !!productDetailsPage.emergencyNumber ||
        !!productDetailsPage.moreInformationArticle) && (
        <Accordion
          startsOpen
          title={t('product_page.toxicological_information.title', 'Toxicological information')}
        >
          <div className={styles.container + ' ' + styles.toxicologicalSection}>
            <div className={styles.leftSide}>
              <div>
                {renderToxicologicalDangerLabel(product.toxicologicalDangerLabel)}
                {!!product.toxicologicalGHSPictograms &&
                  product.toxicologicalGHSPictograms.length > 0 && (
                    <div className={styles.pictogramsContainer}>
                      {product.toxicologicalGHSPictograms.map((pictogram, i) => (
                        <span key={i}>{renderPictogram(pictogram)}</span>
                      ))}
                    </div>
                  )}
              </div>
            </div>
            <div className={styles.rightSide}>
              <div className={styles.ghsCodesSection}>
                {!!product.ghsCodesAndTexts && product.ghsCodesAndTexts.length > 0 && (
                  <div className={styles.ghsCodesContainer}>
                    {HIDDEN_FEATURES.includes('ghsCodes') ? (
                      <div className={styles.hiddenCodesInfo}>
                        {t(
                          'product_page.toxicological_information.check_security_sheet_info',
                          'Please check the security sheet for more information.',
                        )}
                      </div>
                    ) : (
                      product.ghsCodesAndTexts.map(getFinalGhsCodeAndDescription).map((ghs, i) => (
                        <div key={i} className={styles.ghsCodeAndDescription}>
                          <div className={styles.codeContainer}>
                            <span className={styles.code}>{ghs.code}</span>
                          </div>
                          <div className={styles.descriptionContainer}>
                            <span className={styles.info}>{ghs.description}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {/* {HIDDEN_FEATURES.includes('ghsCodes')
                  ? t(
                      'ghsCodes_hidding_information',
                      'Please check the security sheet for more information',
                    )
                  : !!product.ghsCodesAndTexts &&
                    product.ghsCodesAndTexts.length > 0 && (
                      <div className={styles.ghsCodesContainer}>
                        {product.ghsCodesAndTexts
                          .map(getFinalGhsCodeAndDescription)
                          .map((ghs, i) => (
                            <div key={i} className={styles.ghsCodeAndDescription}>
                              <div className={styles.codeContainer}>
                                <span className={styles.code}>{ghs.code}</span>
                              </div>
                              <div className={styles.descriptionContainer}>
                                <span className={styles.info}>{ghs.description}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    )} */}

                <div className={styles.emergencySection}>
                  <div className={styles.emergencyInfoContainer}>
                    <div className={styles.emergencyIconContainer}>
                      <FontAwesomeIcon icon="info-circle" className={styles.infoIcon} />
                    </div>
                    <div className={styles.descriptionContainer}>
                      <span className={styles.emergencyInfo}>
                        {t(
                          'product_page.toxicological_information.notice',
                          'In case of intoxication, contact Anti-Venom Center',
                        )}
                      </span>
                    </div>
                  </div>

                  {(!!productDetailsPage.emergencyNumber ||
                    !!productDetailsPage.moreInformationArticle) && (
                    <div className={styles.emergency}>
                      {!!productDetailsPage.emergencyNumber && (
                        <div className={styles.emergencyContentInfo}>
                          <span className={styles.label}>
                            {t(
                              'product_page.toxicological_information.antivenom_phone_label',
                              'Anti-venom help',
                            )}
                          </span>
                          <span>{productDetailsPage.emergencyNumber}</span>
                        </div>
                      )}
                      {!!productDetailsPage.moreInformationArticle && (
                        <div className={styles.emergencyContentInfo}>
                          <span className={styles.label}>
                            {t(
                              'product_page.toxicological_information.more_information_label',
                              'More information',
                            )}
                          </span>
                          <GenericLink
                            to={
                              '/articles/' + productDetailsPage.moreInformationArticle.slug.current
                            }
                          >
                            {productDetailsPage.moreInformationArticle.title}
                          </GenericLink>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Accordion>
      )}
      {!!(
        product.promotionalVideoUrl ||
        product._rawSummaryText ||
        product._rawMainCharacteristicsText ||
        product._rawObservationsText ||
        product._rawComplementaryInformationsText ||
        product._rawAdvantagesText ||
        product._rawDeclaredContentText ||
        product._rawTrademarkText ||
        product._rawUnstructuredContent
      ) && (
        <Accordion title={t('product_page.description.title', 'Product description')}>
          {!!product.promotionalVideoUrl && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.description.presentation_label', 'Presentation')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.videos}>
                  <Video url={product.promotionalVideoUrl}></Video>
                </div>
              </div>
            </div>
          )}

          {!!product._rawSummaryText && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.description.summary_label', 'Summary')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <BlockContent blocks={product._rawSummaryText} serializers={serializers} />
              </div>
            </div>
          )}

          {!!product._rawMainCharacteristicsText && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.description.main_characteristics_label', 'Main Characteristics')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <BlockContent
                  blocks={product._rawMainCharacteristicsText}
                  serializers={serializers}
                />
              </div>
            </div>
          )}

          {!!product._rawObservationsText && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.description.observations_label', 'Observations')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <BlockContent blocks={product._rawObservationsText} serializers={serializers} />
              </div>
            </div>
          )}

          {!!product._rawComplementaryInformationsText && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t(
                    'product_page.description.complementary_information_label',
                    'Complementary Informations',
                  )}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <BlockContent
                  blocks={product._rawComplementaryInformationsText}
                  serializers={serializers}
                />
              </div>
            </div>
          )}

          {!!product._rawAdvantagesText && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.description.advantages_label', 'Advantages')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <BlockContent blocks={product._rawAdvantagesText} serializers={serializers} />
              </div>
            </div>
          )}

          {!!product._rawDeclaredContentText && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.description.declared_content_label', 'Declared Content')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <BlockContent blocks={product._rawDeclaredContentText} serializers={serializers} />
              </div>
            </div>
          )}

          {!!product._rawTrademarkText && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.description.trademark_label', 'Trademark')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <BlockContent blocks={product._rawTrademarkText} serializers={serializers} />
              </div>
            </div>
          )}

          {!!product._rawUnstructuredContent && (
            <div className={styles.container + ' ' + styles.unstructuredContent}>
              {!!(
                product.promotionalVideoUrl ||
                product._rawSummaryText ||
                product._rawMainCharacteristicsText ||
                product._rawObservationsText ||
                product._rawComplementaryInformationsText ||
                product._rawAdvantagesText ||
                product._rawDeclaredContentText ||
                product._rawTrademarkText
              ) && (
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t('product_page.description.unstructured_content_label', 'More Information')}
                  </h4>
                </div>
              )}
              <div className={styles.rightSide}>
                <BlockContent blocks={product._rawUnstructuredContent} serializers={serializers} />
              </div>
            </div>
          )}
        </Accordion>
      )}
      {!!(
        product._rawApplicationModeText ||
        product._rawPreparationModeText ||
        product._rawBiologicalPrecautionsText ||
        product._rawMinorUsesText ||
        product._rawSafetyIntervalText ||
        product._rawCompatibilitiesText ||
        product._rawSusceptibleWeedsText ||
        product._rawResistantWeedsText
      ) && (
        <Accordion
          title={t('product_page.application_information.title', 'Application information')}
        >
          <div className={styles.wrapper}>
            {!!product._rawApplicationModeText && (
              <div className={styles.container}>
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t(
                      'product_page.application_information.application_mode_label',
                      'Application Mode',
                    )}
                  </h4>
                </div>
                <div className={styles.rightSide}>
                  <BlockContent
                    blocks={product._rawApplicationModeText}
                    serializers={serializers}
                  />
                </div>
              </div>
            )}

            {!!product._rawPreparationModeText && (
              <div className={styles.container}>
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t(
                      'product_page.application_information.preparation_mode_label',
                      'Preparation Mode',
                    )}
                  </h4>
                </div>
                <div className={styles.rightSide}>
                  <BlockContent
                    blocks={product._rawPreparationModeText}
                    serializers={serializers}
                  />
                </div>
              </div>
            )}

            {!!product._rawBiologicalPrecautionsText && (
              <div className={styles.container}>
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t(
                      'product_page.application_information.biological_precautions_label',
                      'Biological Precautions',
                    )}
                  </h4>
                </div>
                <div className={styles.rightSide}>
                  <BlockContent
                    blocks={product._rawBiologicalPrecautionsText}
                    serializers={serializers}
                  />
                </div>
              </div>
            )}

            {!!product._rawMinorUsesText && (
              <div className={styles.container}>
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t('product_page.application_information.minor_uses_label', 'Minor Uses')}
                  </h4>
                </div>
                <div className={styles.rightSide}>
                  <BlockContent blocks={product._rawMinorUsesText} serializers={serializers} />
                </div>
              </div>
            )}

            {!!product._rawSafetyIntervalText && (
              <div className={styles.container}>
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t(
                      'product_page.application_information.safety_interval_label',
                      'Safety Interval',
                    )}
                  </h4>
                </div>
                <div className={styles.rightSide}>
                  <BlockContent blocks={product._rawSafetyIntervalText} serializers={serializers} />
                </div>
              </div>
            )}

            {!!product._rawCompatibilitiesText && (
              <div className={styles.container}>
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t(
                      'product_page.application_information.compatibilities_label',
                      'Compatibilities',
                    )}
                  </h4>
                </div>
                <div className={styles.rightSide}>
                  <BlockContent
                    blocks={product._rawCompatibilitiesText}
                    serializers={serializers}
                  />
                </div>
              </div>
            )}

            {!!product._rawSusceptibleWeedsText && (
              <div className={styles.container}>
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t(
                      'product_page.application_information.susceptible_weeds_label',
                      'Susceptible Weeds',
                    )}
                  </h4>
                </div>
                <div className={styles.rightSide}>
                  <BlockContent
                    blocks={product._rawSusceptibleWeedsText}
                    serializers={serializers}
                  />
                </div>
              </div>
            )}

            {!!product._rawResistantWeedsText && (
              <div className={styles.container}>
                <div className={styles.leftSide}>
                  <h4 className={styles.subTitle}>
                    {t(
                      'product_page.application_information.resistant_weeds_label',
                      'Resistant Weeds',
                    )}
                  </h4>
                </div>
                <div className={styles.rightSide}>
                  <BlockContent blocks={product._rawResistantWeedsText} serializers={serializers} />
                </div>
              </div>
            )}
          </div>
        </Accordion>
      )}
      {!['fr', 'br'].includes(COUNTRY_CODE) && renderApplicationsByCrop()}
      {['fr', 'br'].includes(COUNTRY_CODE) && renderApplicationsFr()}

      {(relatedNewsArticles.length > 0 ||
        (product.gallery &&
          (product.gallery.galleryImages.length > 0 ||
            product.gallery.galleryVideos.length > 0))) && (
        <Accordion title={t('product_page.media_gallery.title', 'News and Gallery')}>
          {relatedNewsArticles?.length > 0 && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.media_gallery.newsLabel', 'News')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.newsWrapper}>
                  {relatedNewsArticles.map((newsArticle, i) => (
                    <div className={styles.news} key={i}>
                      <NewsCard key={i} article={newsArticle} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {product.gallery?.galleryVideos?.length > 0 && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.media_gallery.videosLabel', 'Videos')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.videosWrapper}>
                  {product.gallery.galleryVideos.map((video, i) => (
                    <div className={styles.video} key={i}>
                      <Video url={video.url}></Video>
                      <span className={styles.videoTitle}>{video.name}</span>
                      <p className={styles.videoDescription}>{video.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {product.gallery?.galleryImages?.length > 0 && (
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <h4 className={styles.subTitle}>
                  {t('product_page.media_gallery.imagesLabel', 'Images')}
                </h4>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.imagesWrapper}>
                  {product.gallery.galleryImages.map((image, i) => (
                    <ImageDownload
                      label1={image.label1}
                      label2={image.label2}
                      image={image.image}
                      key={i}
                      className={styles.imageContainer}
                    ></ImageDownload>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Accordion>
      )}

      {!!product.otherDocuments && product.otherDocuments.length > 0 && (
        <Accordion title={t('product_page.other_documents.title', 'Other Documents')}>
          <div className={styles.container + ' ' + styles.otherDocumentsContainer}>
            <div className={styles.leftSide}></div>
            <div className={styles.rightSide}>
              {product.otherDocuments.map((document, i) => (
                <FileRow
                  key={i}
                  small
                  title={document.title}
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  url={document.file.file!.asset!.url}
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  size={document.file.file!.asset!.size}
                ></FileRow>
              ))}
            </div>
          </div>
        </Accordion>
      )}
      {!!productDetailsPage._rawLegalNotice && (
        <Accordion title={t('product_page.legal_notice.title', 'Legal Notice')}>
          <div className={styles.container}>
            <div className={styles.leftSide}></div>
            <div className={styles.rightSide}>
              <div className={styles.legalNotice}>
                <BlockContent
                  blocks={productDetailsPage._rawLegalNotice}
                  serializers={serializers}
                />
              </div>
            </div>
          </div>
        </Accordion>
      )}

      {relatedProducts.length > 0 && (
        <Accordion startsOpen title={t('product_page.related_products.title', 'Related Products')}>
          <div className={styles.container}>
            <div className={styles.relatedProductsContainer}>
              {relatedProducts.map(relatedProduct => (
                <Link
                  key={relatedProduct._id}
                  className={styles.cardLinkWrapper}
                  to={`/products/${relatedProduct.slug.current}`}
                >
                  <ProductCard product={relatedProduct}></ProductCard>
                </Link>
              ))}
              {Array.from({ length: (3 - (relatedProducts.length % 3)) % 3 }).map(
                (__, i: number) => (
                  <div key={i} className={styles.mockEvent}></div>
                ),
              )}
            </div>
          </div>
        </Accordion>
      )}

      {!!productDetailsPage.disclaimer && (
        <div className={styles.disclaimerContainer}>
          <div className={styles.leftSide}>
            <h4 className={styles.subTitle}>{t('product_page.disclaimer.title', 'Disclaimer')}</h4>
          </div>
          <div className={styles.rightSide}>
            <p>{productDetailsPage.disclaimer}</p>
            {!!productDetailsPage.disclaimerArticle && (
              <GenericLink
                to={'/articles/' + productDetailsPage.disclaimerArticle.slug.current}
                className={styles.disclaimerLink}
              >
                {productDetailsPage.disclaimerArticle.title} &gt;
              </GenericLink>
            )}
          </div>
        </div>
      )}
    </DetailsPageLayout>
  );
};

export default ProductPage;
